import { getStringValue } from "../services/util";

import { TAB_NAMES } from "../components/strings";

import {
  isUserAuthenticated,
  isUserAdmin,
  isUserContentAdmin,
  isUserTeacher,
  isUserTeamManager,
  isUserCustomerService,
  hasPrimaryInstructor,
  isUserCompanyManager,
  isSelfStudyUser,
  isGroupUser,
  isOneOnOneUser,
  isUserStudent,
} from "../services/authUtil";
import { ReactComponent as SettingsOutlinedIcon } from "./../images/settings_outlined_icon.svg";
import { ReactComponent as BarIcon } from "./../images/bar_chart.svg";
import { ReactComponent as HelpOutlinedIcon } from "./../images/help_outlined_icon.svg";
import { ReactComponent as LearningsIcon } from "./../images/learnings_nav_icon.svg";
import { ReactComponent as LibraryIcon } from "./../images/library_nav_icon.svg";
import { ReactComponent as HomeIcon } from "images/home.svg";
import { ReactComponent as GlobalIcon } from "images/global.svg";
import { AdaptiveTestStatus } from "model/adaptive_test";
import useUser from "hooks/useUser";
import { ReactElement, useMemo } from "react";

//-- the website tabs (common for TabsPanel and SideMenu) --
function getTabName(key: string) {
  if (isUserAdmin() && key === "settings") {
    return "Settings";
  } else {
    return getStringValue(TAB_NAMES, key);
  }
}

export type Tab = {
  name: string;
  title: string;
  path?: string;
  icon?: ReactElement;
  asIcon?: boolean;
  absolutePath?: string;
  external?: boolean;
  newTab?: boolean;
  onClick?: () => void;
};

const TABS: Record<string, Tab> = {
  MY_DASHBOARD: {
    name: "my_dashboard",
    title: "Common." + getTabName("myDashboard"),
    path: "/",
    icon: <HomeIcon />,
  },
  EXERCISES: {
    name: "exercises",
    title: getTabName("exercises"),
    path: "/exercises",
  },
  MY_PROGRESS: {
    name: "myProgress",
    title: "Common." + getTabName("myProgress"),
    path: "/my_progress",
    icon: <LearningsIcon />,
  },
  TOOLS: {
    name: "tools",
    title: "Common." + getTabName("tools"),
  },
  SETTINGS: {
    name: "settings",
    title: "Common." + getTabName("settings"),
    path: "/settings",
    asIcon: true,
    icon: <SettingsOutlinedIcon />,
  },
  LEVEL_TEST: {
    name: "levelTest",
    title: "Common." + getTabName("levelTest"),
    path: "/level-test",
    asIcon: true,
    icon: <BarIcon />,
  },
  RETAKE_LEVEL_TEST: {
    name: "retakeLevelTest",
    title: "Common." + getTabName("retakeLevelTest"),
    path: "/level-test",
    asIcon: true,
    icon: <BarIcon />,
  },
  HELP: {
    name: "help",
    title: "Common." + getTabName("help"),
    absolutePath: "http://help.talaera.com",
    external: true,
    asIcon: true,
    newTab: true,
    icon: <HelpOutlinedIcon />,
  },
  LANGUAGES: {
    name: "language",
    title: getTabName("language"),
    asIcon: true,
    icon: <GlobalIcon />,
  },
  TEACHER_RESOURCES: {
    name: "teacher_resources",
    title: getTabName("teacherResources"),
    absolutePath: "https://talaera.lp.page/talaera-teacher-hub",
    external: true,
  },
  TEAM_DASHBOARD: {
    name: "team_dashboard",
    title: "Common." + getTabName("teamDashboard"),
    path: "/teams",
  },
  LIBRARY: {
    name: "library",
    title: "Common." + getTabName("library"),
    path: "/library",
    icon: <LibraryIcon />,
  },
  ADMIN_DASHBOARD: {
    name: "admin_dashboard",
    title: "Common." + getTabName("adminDashboard"),
    path: "/admin",
  },
  ADMIN_SETTINGS: {
    name: "admin_settings",
    title: "Common." + getTabName("settings"),
    path: "/admin/settings",
  },
  SIGN_IN: {
    name: "sign_in",
    title: getTabName("signIn"),
    path: "/sign_in",
  },
  STUDENTS: {
    name: "students",
    title: getTabName("students"),
    path: "/students",
  },
  ADMIN_CONTENT: {
    name: "content",
    title: "Common." + getTabName("content"),
    path: "/admin/content",
  },
};

export function useTabs(params?: Record<string, Record<string, any>>): Tab[] {
  const user = useUser();
  return useMemo(() => {
    const tabs = [];
    if (!isUserAuthenticated()) {
      tabs.push(TABS.SIGN_IN);
    } else {
      if (isUserContentAdmin()) {
        tabs.push(TABS.ADMIN_DASHBOARD);
        tabs.push(TABS.ADMIN_CONTENT);
        tabs.push(TABS.LIBRARY);
      } else if (isUserAdmin()) {
        tabs.push(TABS.ADMIN_DASHBOARD);
        tabs.push(TABS.ADMIN_CONTENT);
        tabs.push(TABS.LIBRARY);
        tabs.push(TABS.ADMIN_SETTINGS);
      } else if (isUserCustomerService()) {
        // No tabs for this role.
      } else {
        if (!isUserTeamManager() || hasPrimaryInstructor()) {
          // We assume that if team manager has a primary instructor
          // then she can do whatever student can do.
          if (!isGroupUser()) {
            tabs.push(TABS.MY_DASHBOARD);
          }

          if (isUserTeacher()) {
            tabs.push(TABS.TEACHER_RESOURCES);
            tabs.push(TABS.EXERCISES);
            tabs.push(TABS.STUDENTS);
          } else {
            if (isGroupUser()) {
              tabs.push(TABS.LIBRARY);
            }
          }
        }

        if (isSelfStudyUser() || isGroupUser() || isOneOnOneUser()) {
          tabs.push(TABS.MY_PROGRESS);
        }

        if (!isGroupUser()) {
          tabs.push(TABS.LIBRARY);
        }

        if (isUserTeamManager() || isUserCompanyManager()) {
          tabs.push(TABS.TEAM_DASHBOARD);
        }

        tabs.push(TABS.TOOLS);
        tabs.push(TABS.SETTINGS);

        if (isUserStudent(user) && user?.features?.adaptive_testing) {
          if (user?.student?.adaptive_test?.status !== AdaptiveTestStatus.completed) {
            tabs.push(TABS.LEVEL_TEST);
          } else {
            tabs.push(TABS.RETAKE_LEVEL_TEST);
          }
        }

        tabs.push(TABS.HELP);
        tabs.push(TABS.LANGUAGES);
      }
    }

    return tabs.map(tab => {
      if (params && params[tab.name]) {
        return Object.assign({}, tab, params[tab.name]);
      } else {
        return tab;
      }
    });
  }, [user, params]);
}
